import * as React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { vw } from "../../../lib/utils/responsiveUtils";
import { useMainGolfParkSearch } from "../../../@apis/hooks/useMain";
import { staticURL } from "../../../lib/utils/imageUtils";
import type { GolfParkSearchResponse } from "../../../@apis/dto/response/main.response";
import { useGeolocation } from "../../../hooks/useGeolocation";
import { useDeviceStore } from "../../../lib/store/useDeviceStore";
import { useGeoStore } from "../../../lib/store/useGeoStore";
import { fetchIpLocation } from "../../../lib/utils/ipLocation";
// import { useModalStore } from "../../../lib/store/useModalStore";

interface Props {
  onClick?: (item: GolfParkSearchResponse) => void;
  // 무한 스크롤을 감지할 스크롤 컨테이너. 미지정 시 페이지의 <main>을 사용한다.
  scrollRoot?: HTMLElement | null;
}

// 데스크톱 웹은 고정밀(GPS) 측위가 느리거나 실패하기 쉬워, 빠르게 응답하도록 완화한다.
const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 15000,
  maximumAge: 60000,
};

export default function SearchLayout({ onClick, scrollRoot }: Props) {
  const { status, requestOnce } = useGeolocation(GEO_OPTIONS);
  const { deviceType } = useDeviceStore();
  const setCoords = useGeoStore((s) => s.setCoords);
  // const { openModal } = useModalStore();

  // 좌표: 마지막으로 저장된 위치(store)로 초기화해, 페이지를 다시 와도 유지한다.
  const [lat, setLat] = React.useState<number | undefined>(
    () => useGeoStore.getState().coords?.latitude,
  );
  const [lng, setLng] = React.useState<number | undefined>(
    () => useGeoStore.getState().coords?.longitude,
  );

  // 검색어
  const [typing, setTyping] = React.useState("");
  const [q, setQ] = React.useState("");

  // 검색어 초기화 + 로컬/전역 좌표 반영
  const applyCoords = React.useCallback(
    (latitude?: number, longitude?: number) => {
      if (latitude == null || longitude == null) return;
      setTyping(""); // 인풋 지우기
      setQ(""); // 실제 쿼리값도 지우기(이게 핵심)
      setLat(latitude);
      setLng(longitude);
      // 홈(useGetMain) 등 다른 화면에서 재사용하도록 좌표를 전역에 저장
      setCoords({ latitude, longitude });
    },
    [setCoords],
  );

  // 버튼 클릭 시: 누를 때마다 최신 위치를 새로 받아 좌표를 갱신한다.
  // 위치 수신 콜백에서 직접 저장해 권한 허용·매 탭마다 확실히 반영한다.
  const handleFindLocation = () => {
    if (deviceType == "ios" && status == "denied") {
      window.webkit.messageHandlers.onLocationPermissionDenied.postMessage(
        JSON.stringify({}),
      );
      return;
    }

    requestOnce(
      (pos) => {
        applyCoords(pos.coords.latitude, pos.coords.longitude);
      },
      async () => {
        // PC 웹은 GPS가 없어 표준 측위가 실패할 수 있다. 이 경우 IP 기반
        // 대략 좌표로 폴백한다. (앱/모바일은 GPS를 그대로 사용)
        if (deviceType === "web") {
          const ip = await fetchIpLocation();
          if (ip) applyCoords(ip.latitude, ip.longitude);
        }
      },
    );
  };
  const listRef = React.useRef<HTMLDivElement>(null);

  // 검색어 없으면 pageSize 안 보냄(= 전체)
  const {
    data: res,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...useMainGolfParkSearch({
      name: q || undefined,
      latitude: lat,
      longitude: lng,
    }),
  });
  const golfParkPages = res?.pages || [];

  const triggerRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRoot ?? document.querySelector("main"), threshold: 0 },
    );

    const currentTrigger = triggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, scrollRoot]);

  React.useEffect(() => {
    const t = setTimeout(() => setQ(typing.trim()), 500);
    return () => clearTimeout(t);
  }, [typing]);

  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <section className="mb-[24px] px-[15px] space-y-[12px]">
        {/* 검색창 */}
        <div className="rounded-[10px] border border-[#e6e6e6] px-[15px] flex items-center gap-[10px] bg-white py-[6px]">
          <img
            className="size-[20px] shrink-0"
            src="/icons/ic-search.webp"
            alt="검색"
          />
          <input
            ref={inputRef}
            autoFocus
            value={typing}
            onChange={(e) => setTyping(e.target.value)}
            placeholder="골프장명을 입력해주세요."
            className="flex-1 min-w-0 outline-none placeholder:text-[#ccc] text-[0.7rem] placeholder:text-[0.7rem]"
          />
        </div>

        {/* 현재 위치로 찾기 */}
        <button
          onClick={handleFindLocation} // 버튼 클릭 시 좌표 세팅
          className="h-[50px] w-full rounded-[10px] border border-[#e6e6e6] px-[15px] flex-row justify-center bg-white"
          style={{ height: vw(50) }}
          type="button"
        >
          <div className="flex items-center gap-x-[2px]">
            <img className="size-5" src="/icons/ic-challenge.webp" alt="" />
            <span className="text-[#222] text-sm">현재 위치로 찾기</span>
          </div>
        </button>
      </section>

      {/* 리스트 영역 */}
      <section
        className="flex-1 pt-[25px] px-[15px] space-y-3 flex flex-col bg-[#f5f5f5]  min-h-screen"
        ref={listRef}
      >
        {golfParkPages.every(
          (p) => (p.data.golfParkList ?? []).length === 0,
        ) ? (
          <div className="text-center text-[#888] py-[20px]">
            검색 결과가 없습니다.
          </div>
        ) : (
          golfParkPages.map((pageData, pageIndex) => (
            <React.Fragment key={pageIndex}>
              {(pageData.data.golfParkList || []).map((item, idx, arr) => {
                const isLast =
                  pageIndex === golfParkPages.length - 1 &&
                  idx === arr.length - 1;
                return (
                  <div
                    key={item.code}
                    onClick={() => onClick?.(item)}
                    className="bg-white rounded-[15px] p-[20px] mb-[15px] flex items-center gap-[19px]"
                    ref={isLast ? triggerRef : undefined}
                  >
                    <img
                      src={
                        item.imageUrl
                          ? staticURL(item.imageUrl)
                          : "/images/img-login-logo.webp"
                      }
                      alt={item.name}
                      className="rounded-[10px] object-cover bg-[#eee] shrink-0"
                      style={{ width: vw(83), height: vw(83) }}
                    />

                    <div className="space-y-[6px] min-w-0 flex-1">
                      <p className="font-bold text-[#222] line-clamp-1 text-sm">
                        {item.name}
                      </p>
                      <div className="relative flex gap-x-[4px] min-w-0">
                        <img
                          src="/icons/ic-place-gray.webp"
                          alt="place"
                          className="shrink-0 mt-1 size-3.5"
                        />
                        <p className="text-[#888] font-[500] leading-[1.5] line-clamp-2 text-sm break-keep whitespace-pre-line break-words min-w-0 flex-1">
                          {item.address}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))
        )}
      </section>
    </>
  );
}
